import { Box, Button, Center, Checkbox, CheckboxGroup, FormControl, FormLabel, HStack, Image, Stack, Text, VStack, useColorModeValue } from '@chakra-ui/react';
import HtmlToReact from 'html-to-react';
import { parsePhoneNumber } from 'libphonenumber-js/min';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import ModalLoading from '../../components/Loader/ModalLoader.jsx';
import { trackRegistWaiver } from '../../helper/utils/eventTrackGA.js';
import { isEmptyObject } from '../../helper/utils/isEmptyObject.js';
import { urlNavigate } from '../../helper/utils/navigation.js';
import getStaticAsset from '../../helper/utils/utilsStaticAsset';
import { waiverEN, waiverID } from '../../mock/waiver.js';
import { doSubmission } from '../../redux/waiver/waiver.actions.js';

const HtmlToReactParser = HtmlToReact.Parser;
function FormAgreement() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const page = JSON.parse(localStorage.getItem('page') || '{}');

    const { t, i18n } = useTranslation();
    const { data = {}, minor = [], loadingSubmission = false } = useSelector((s) => s?.waiver);

    const ref = useRef(null);
    const [signature, setSignature] = useState('');
    const [checkbox, setCheckbox] = useState([]);
    const [checkboxSubscribe, setCheckboxSubscribe] = useState(true);

    const onEnd = () => {
        const sig = ref?.current?.getTrimmedCanvas().toDataURL('image/png');
        setSignature(sig);
    };

    const clear = () => {
        ref?.current?.clear();
        setSignature('');
    };

    useEffect(() => {
        const resizeCanvas = () => {
            if (ref.current) {
                const canvas = ref.current.getCanvas();
                const ratio = Math.max(window.devicePixelRatio || 1, 1);

                const data = ref.current.toData();

                canvas.width = canvas.offsetWidth * ratio;
                canvas.height = canvas.offsetHeight * ratio;
                canvas.getContext('2d').scale(ratio, ratio);

                ref.current.fromData(data);
            }
        };

        window.addEventListener('resize', resizeCanvas);
        resizeCanvas();

        return () => {
            window.removeEventListener('resize', resizeCanvas);
        };
    }, []);

    const onChangeCheckbox = (e) => setCheckbox(e);
    const onChangeCheckBoxSubscribe = (e) => setCheckboxSubscribe(e?.target?.checked);

    const handleSubmit = () => {
        const payloadFromLocalDisk = JSON.parse(localStorage.getItem('payload'));
        const minors = minor.length === 0 ? JSON.parse(localStorage.getItem('minor') ?? '[]') : minor;

        const payload = {
            ...payloadFromLocalDisk,
            gender: (data.gender || '') === 'male' ? '1' : '0',
            signature,
            minor: minors.map((el) => ({ ...el, parentId: data?.phoneNo ?? '' })),
            email: data.email || '',
            isSubscribed: checkboxSubscribe ? '1' : '0',
            dialCode: `+${parsePhoneNumber(data?.phoneNo ?? '', 'ID')?.countryCallingCode ?? ''}`,
            address: data.address || '',
            telp: data.phoneNo || '',
            fullName: data.fullname || '',
            birthDate: data.birthDate || '',
            city: data.city || '9999',
            country: data.country || 'ID',
        };

        dispatch(
            doSubmission(payload, null, () => {
                navigate(urlNavigate('register-email'));
                trackRegistWaiver({
                    label: 'user sign agreement',
                });
            }),
        );
    };

    const handleCancel = () => {
        navigate(urlNavigate('waiver', 'add-children'));
        clear();
        setCheckbox([]);
    };

    const inputHtml = () => {
        let text;
        switch (i18n.language) {
            case 'id':
                text = waiverID;
                break;
            case 'en':
                text = waiverEN;
                break;
            default:
                text = '';
        }
        return text;
    };

    const htmlToReactParser = new HtmlToReactParser();
    const reactElement = htmlToReactParser.parse(inputHtml());

    if (isEmptyObject(data)) {
        return <Navigate to={urlNavigate('waiver', 'add-children')} />;
    }

    return (
        <Box
            minH='100vh'
            w='full'
            bgImage={getStaticAsset(page['home']['image'])}
            bgSize='cover'
            bgPosition='center'
            bgRepeat='no-repeat'
            position='relative'
            _before={{
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bg: 'rgba(0, 0, 0, 0.5)',
                zIndex: 0,
            }}>
            <ModalLoading loading={loadingSubmission} />

            <Center minH='100vh' position='relative' zIndex={1} p={4} py={8}>
                <Box maxW='800px' w='full' bg={useColorModeValue('white', 'gray.800')} rounded='2xl' boxShadow='2xl' p={{ base: 6, md: 8 }} backdropFilter='blur(10px)'>
                    {/* Logo */}
                    <Center mb={6}>
                        <Image src={getStaticAsset('dream_us_logo.webp')} alt='Dream Us Logo' w={{ base: '60%', md: '40%' }} maxW='200px' />
                    </Center>

                    {/* Agreement Card */}
                    <VStack spacing={6} align='stretch'>
                        {/* Instruction */}
                        <Box p={3} bg='primary.50' border='1px' borderColor='primary.200' rounded='lg' textAlign='center'>
                            <Text fontSize='lg' fontWeight={600} color='primary.700'>
                                Please sign & agree before purchase
                            </Text>
                        </Box>

                        {/* Waiver Content*/}
                        <Box
                            maxH='400px'
                            overflowY='auto'
                            p={5}
                            rounded='lg'
                            border='1px'
                            borderColor='gray.200'
                            bg='white'
                            css={{
                                '&::-webkit-scrollbar': {
                                    width: '8px',
                                },
                                '&::-webkit-scrollbar-track': {
                                    background: '#f1f1f1',
                                    borderRadius: '10px',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    background: '#888',
                                    borderRadius: '10px',
                                },
                                '&::-webkit-scrollbar-thumb:hover': {
                                    background: '#555',
                                },
                            }}>
                            <Box
                                sx={{
                                    '& h2': {
                                        fontSize: 'lg',
                                        fontWeight: 'bold',
                                        mb: 2,
                                    },
                                    '& p': {
                                        mb: 2,
                                        fontSize: 'sm',
                                    },
                                    '& ul, & ol': {
                                        pl: 5,
                                        mb: 2,
                                    },
                                    '& li': {
                                        mb: 1,
                                        fontSize: 'sm',
                                    },
                                }}>
                                {reactElement}
                            </Box>

                            {/* Checkbox for Agreement*/}
                            <Box mt={6} pt={4} borderTop='2px' borderColor='gray.300'>
                                <FormControl>
                                    <CheckboxGroup colorScheme='primary' value={checkbox} onChange={onChangeCheckbox}>
                                        <Stack spacing={3}>
                                            <Checkbox value='1' size='md'>
                                                <Text fontSize='sm'>{t('disparity')}</Text>
                                            </Checkbox>
                                            <Checkbox value='2' size='md'>
                                                <Text fontSize='sm'>{t('confirm')}</Text>
                                            </Checkbox>
                                        </Stack>
                                    </CheckboxGroup>
                                </FormControl>

                                {/* Signature Box for Agreement*/}
                                <Box mt={5}>
                                    <FormLabel fontSize='md' fontWeight={600} mb={3}>
                                        {t('sign_here')}
                                    </FormLabel>
                                    <Box bg='white' border='2px' borderColor='gray.300' borderStyle='dashed' rounded='md' overflow='hidden' position='relative'>
                                        <SignatureCanvas
                                            backgroundColor='#FFFFFF'
                                            penColor='black'
                                            ref={ref}
                                            onEnd={onEnd}
                                            canvasProps={{
                                                className: 'sigCanvas',
                                                style: {
                                                    width: '100%',
                                                    height: '300px', // Set a fixed height in CSS/style so it has dimension
                                                },
                                            }}
                                        />
                                    </Box>
                                    <Button mt={3} size='sm' colorScheme='gray' variant='outline' onClick={clear} isDisabled={signature === ''}>
                                        {t('clear')}
                                    </Button>
                                </Box>
                            </Box>
                        </Box>

                        {/* Subscribe Checkbox */}
                        <FormControl>
                            <Checkbox isChecked={checkboxSubscribe} onChange={onChangeCheckBoxSubscribe} size='md'>
                                <Text fontSize='sm'>{t('subscribe')}</Text>
                            </Checkbox>
                        </FormControl>

                        {/* Action Buttons */}
                        <HStack spacing={4} pt={4}>
                            <Button w='full' size='lg' fontSize='md' variant='outline' colorScheme='gray' onClick={handleCancel}>
                                Back
                            </Button>
                            <Button isLoading={loadingSubmission} w='full' size='lg' fontSize='md' colorScheme='primary' isDisabled={checkbox.length !== 2 || signature === ''} onClick={handleSubmit}>
                                {t('agree')}
                            </Button>
                        </HStack>
                    </VStack>
                </Box>
            </Center>
        </Box>
    );
}

export default FormAgreement;
